import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useQuery } from '@apollo/client';
import { ArrowDown, ArrowUp, Download, Loader2 } from 'lucide-react';
import Layout from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { ADMIN_AI_ASSISTANT_USAGE } from '@/queries/aiAssistant';
import {
  DEFAULT_BRL_RATE, buildAiUsageCsv, formatBrl, formatUsd, parseBrlRate, usdToBrl, type AiUsageReport,
} from '@/lib/ai-usage/aiUsage';

const RATE_KEY = 'autoclipper:admin-ai-usage:brl-rate';
const PAGE_SIZE = 50;
const PRESETS = [7, 30, 90] as const;
type Sort = 'cost_usd' | 'messages' | 'model_calls' | 'prompt_tokens' | 'completion_tokens' | 'last_used_at';

const spToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
const daysBefore = (day: string, days: number) => {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
};
const readRate = () => {
  try {
    return window.localStorage.getItem(RATE_KEY) ?? String(DEFAULT_BRL_RATE);
  } catch {
    return String(DEFAULT_BRL_RATE);
  }
};

function Kpi({ label, value, testId }: { label: string; value: ReactNode; testId?: string }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="space-y-1 p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

// Admin report of the AI assistant's model calls: tokens and estimated cost
// per user, model and São Paulo day (backend adminAiAssistantUsage).
export default function AdminAiUsage() {
  const { t, i18n } = useTranslation();
  const client = useApolloClient();
  const { toast } = useToast();
  const locale = i18n.language === 'en' ? 'en-US' : 'pt-BR';
  const [to, setTo] = useState(spToday);
  const [from, setFrom] = useState(() => daysBefore(spToday(), 29));
  const [rateInput, setRateInput] = useState(readRate);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('cost_usd');
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const rate = parseBrlRate(rateInput);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    try {
      window.localStorage.setItem(RATE_KEY, rateInput);
    } catch {
      // Per-browser convenience only.
    }
  }, [rateInput]);

  const variables = {
    from, to, search: search || null, sort, direction, page, pageSize: PAGE_SIZE,
  };
  const { data, loading, error, refetch } = useQuery<{ adminAiAssistantUsage: AiUsageReport }>(
    ADMIN_AI_ASSISTANT_USAGE,
    { variables },
  );
  const report = data?.adminAiAssistantUsage;
  const totals = report?.totals;
  const pages = report ? Math.max(1, Math.ceil(report.total_rows / PAGE_SIZE)) : 1;
  const number = (value: number) => new Intl.NumberFormat(locale).format(value);
  const compact = (value: number) => new Intl.NumberFormat(locale, { notation: 'compact' }).format(value);
  const money = (usd: number) => `${formatBrl(usdToBrl(usd, rate), locale)} · ${formatUsd(usd, locale)}`;
  const per = (usd: number, count: number) => (count > 0 ? money(usd / count) : '—');
  const maxDayCost = useMemo(
    () => Math.max(0, ...(report?.by_day ?? []).map((day) => day.cost_usd)),
    [report],
  );

  const toggleSort = (next: Sort) => {
    if (sort === next) setDirection(direction === 'desc' ? 'asc' : 'desc');
    else {
      setSort(next);
      setDirection('desc');
    }
    setPage(1);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const result = await client.query<{ adminAiAssistantUsage: AiUsageReport }>({
        query: ADMIN_AI_ASSISTANT_USAGE,
        variables: { ...variables, page: 1, pageSize: 1000 },
        fetchPolicy: 'no-cache',
      });
      const rows = result.data?.adminAiAssistantUsage?.rows;
      if (!rows) throw new Error('no_rows');
      const csv = buildAiUsageCsv(rows, { brlRate: rate });
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai-usage-${from}-${to}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: t('admin.aiUsage.exportError'), variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const sortHeader = (key: Sort, label: string) => (
    <TableHead className="text-right">
      <button type="button" className="inline-flex items-center gap-1" onClick={() => toggleSort(key)}>
        {label}
        {sort === key && (direction === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
      </button>
    </TableHead>
  );

  return (
    <Layout>
      <div className="space-y-6 p-4 md:p-6">
        <div>
          <h1 className="text-2xl font-bold">{t('admin.aiUsage.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('admin.aiUsage.subtitle')}</p>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>{t('admin.aiUsage.from')}</span>
            <Input type="date" value={from} max={to} data-testid="ai-usage-from" onChange={(event) => { setFrom(event.target.value); setPage(1); }} />
          </label>
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>{t('admin.aiUsage.to')}</span>
            <Input type="date" value={to} min={from} data-testid="ai-usage-to" onChange={(event) => { setTo(event.target.value); setPage(1); }} />
          </label>
          <div className="flex gap-1">
            {PRESETS.map((days) => (
              <Button key={days} size="sm" variant="outline" onClick={() => { setFrom(daysBefore(to, days - 1)); setPage(1); }}>
                {t(`admin.aiUsage.presets.d${days}`)}
              </Button>
            ))}
          </div>
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>{t('admin.aiUsage.brlRate')}</span>
            <Input className="w-24" inputMode="decimal" value={rateInput} data-testid="ai-usage-rate" onChange={(event) => setRateInput(event.target.value)} />
          </label>
          <Input className="w-64" placeholder={t('admin.aiUsage.search')} value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
          <Button className="ml-auto" variant="outline" onClick={() => { void exportCsv(); }} disabled={exporting || !report} data-testid="ai-usage-export">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {t('admin.aiUsage.exportCsv')}
          </Button>
        </div>

        {error && (
          <Card>
            <CardContent className="flex items-center justify-between gap-3 p-4 text-sm">
              <span>{t('admin.aiUsage.loadError')}</span>
              <Button size="sm" variant="outline" onClick={() => { void refetch(); }}>{t('admin.aiUsage.retry')}</Button>
            </CardContent>
          </Card>
        )}

        {!error && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="ai-usage-kpis">
            {loading || !totals ? (
              Array.from({ length: 8 }, (_, index) => <Skeleton key={index} className="h-20" />)
            ) : (
              <>
                <Kpi label={t('admin.aiUsage.kpis.activeUsers')} value={number(totals.active_users)} />
                <Kpi label={t('admin.aiUsage.kpis.messages')} value={number(totals.messages)} />
                <Kpi label={t('admin.aiUsage.kpis.tokens')} value={`${compact(totals.prompt_tokens)} / ${compact(totals.completion_tokens)}`} />
                <Kpi label={t('admin.aiUsage.kpis.cost')} value={money(totals.cost_usd)} />
                <Kpi label={t('admin.aiUsage.kpis.costPerMessage')} value={per(totals.cost_usd, totals.messages)} />
                <Kpi label={t('admin.aiUsage.kpis.costPerUser')} value={per(totals.cost_usd, totals.active_users)} />
                <Kpi label={t('admin.aiUsage.kpis.failedCalls')} value={number(totals.failed_calls)} />
                {totals.unpriced_calls > 0 && (
                  <p className="col-span-full text-xs text-amber-500">
                    {t('admin.aiUsage.unpriced', { count: totals.unpriced_calls })}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {report && (
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">{t('admin.aiUsage.byDay')}</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {report.by_day.length === 0 && <p className="text-sm text-muted-foreground">{t('admin.aiUsage.empty')}</p>}
                {report.by_day.map((day) => (
                  <div key={day.day} className="flex items-center gap-2 text-xs">
                    <span className="w-20 shrink-0 tabular-nums">{day.day}</span>
                    <div className="h-2 flex-1 rounded bg-muted">
                      <div className="h-2 rounded bg-primary" style={{ width: `${maxDayCost > 0 ? (day.cost_usd / maxDayCost) * 100 : 0}%` }} />
                    </div>
                    <span className="w-10 shrink-0 text-right tabular-nums">{number(day.messages)}</span>
                    <span className="w-40 shrink-0 text-right tabular-nums">{money(day.cost_usd)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">{t('admin.aiUsage.byModel')}</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin.aiUsage.columns.model')}</TableHead>
                      <TableHead>{t('admin.aiUsage.columns.purpose')}</TableHead>
                      <TableHead className="text-right">{t('admin.aiUsage.columns.calls')}</TableHead>
                      <TableHead className="text-right">{t('admin.aiUsage.columns.promptTokens')}</TableHead>
                      <TableHead className="text-right">{t('admin.aiUsage.columns.completionTokens')}</TableHead>
                      <TableHead className="text-right">{t('admin.aiUsage.columns.cost')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.by_model.map((row) => (
                      <TableRow key={`${row.llm_model}-${row.purpose}`}>
                        <TableCell className="font-mono text-xs">{row.llm_model}</TableCell>
                        <TableCell>{t(`admin.aiUsage.purpose.${row.purpose}`, { defaultValue: row.purpose })}</TableCell>
                        <TableCell className="text-right tabular-nums">{number(row.model_calls)}</TableCell>
                        <TableCell className="text-right tabular-nums">{compact(row.prompt_tokens)}</TableCell>
                        <TableCell className="text-right tabular-nums">{compact(row.completion_tokens)}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(row.cost_usd)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}

        {report && (
          <Card>
            <CardHeader><CardTitle className="text-base">{t('admin.aiUsage.users')}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Table data-testid="ai-usage-users">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.aiUsage.columns.user')}</TableHead>
                    <TableHead>{t('admin.aiUsage.columns.plan')}</TableHead>
                    {sortHeader('messages', t('admin.aiUsage.columns.messages'))}
                    {sortHeader('model_calls', t('admin.aiUsage.columns.calls'))}
                    {sortHeader('prompt_tokens', t('admin.aiUsage.columns.promptTokens'))}
                    {sortHeader('completion_tokens', t('admin.aiUsage.columns.completionTokens'))}
                    {sortHeader('cost_usd', t('admin.aiUsage.columns.cost'))}
                    <TableHead className="text-right">{t('admin.aiUsage.columns.costPerMessage')}</TableHead>
                    {sortHeader('last_used_at', t('admin.aiUsage.columns.lastUsed'))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-sm text-muted-foreground">{t('admin.aiUsage.empty')}</TableCell>
                    </TableRow>
                  )}
                  {report.rows.map((row) => (
                    <TableRow key={row.user.id}>
                      <TableCell>
                        <div className="font-medium">{row.user.name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{row.user.email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <span>{row.plan_name || '—'}</span>
                          <Badge variant={row.is_paid ? 'default' : 'outline'} className="text-[10px]">
                            {row.is_paid ? t('admin.aiUsage.paid') : t('admin.aiUsage.free')}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{number(row.messages)}</TableCell>
                      <TableCell className="text-right tabular-nums">{number(row.model_calls)}</TableCell>
                      <TableCell className="text-right tabular-nums">{compact(row.prompt_tokens)}</TableCell>
                      <TableCell className="text-right tabular-nums">{compact(row.completion_tokens)}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(row.cost_usd)}</TableCell>
                      <TableCell className="text-right tabular-nums">{per(row.cost_usd, row.messages)}</TableCell>
                      <TableCell className="text-right text-xs">
                        {row.last_used_at ? new Date(row.last_used_at).toLocaleString(locale) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-end gap-2 text-xs">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</Button>
                <span>{t('admin.aiUsage.page', { page, pages })}</span>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)}>›</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
